import { useEffect, useRef, useState } from "react";
import type { TradeItem } from "./tradeTypes";
import { computeTotal, formatCurrency } from "./tradeHelpers";
import { createTradeProposal } from "./tradeProposalApi";
import { collection, endAt, getDocs, limit, orderBy, query, startAt } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useFocusTrap } from "./useFocusTrap";

type Props = {
  open: boolean;
  proposerUid: string;
  sentByProposer: TradeItem[];
  sentByRecipient: TradeItem[];
  onClose(): void;
  onCreated(id: string): void;
};

type UserLite = { uid: string; username: string; displayName?: string | null };

export default function SendProposalModal({ open, proposerUid, sentByProposer, sentByRecipient, onClose, onCreated }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [qUser, setQUser] = useState("");
  const [users, setUsers] = useState<UserLite[]>([]);
  const [selected, setSelected] = useState<UserLite | null>(null);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const userSearchRef = useRef<HTMLInputElement | null>(null);
  useFocusTrap({ active: open, containerRef: panelRef, initialFocusRef: userSearchRef, onEscape: onClose });

  useEffect(() => {
    if (!open) {
      setErr(null);
      setBusy(false);
      setMessage("");
      setQUser("");
      setUsers([]);
      setSelected(null);
    }
  }, [open]);

  async function searchUsers() {
    const p = qUser.trim().toLowerCase();
    if (!p) return setUsers([]);
    const usernamesRef = collection(db, "usernames");
    const snap = await getDocs(query(usernamesRef, orderBy("username_lower"), startAt(p), endAt(p + "\uf8ff"), limit(10)));
    const result: UserLite[] = [];
    snap.forEach((d) => {
      const v = d.data() as any;
      if (v?.uid) result.push({ uid: v.uid, username: v.username || v.username_lower || d.id, displayName: v?.displayName ?? null });
    });
    setUsers(result);
  }

  async function submit() {
    if (!selected) {
      setErr("Pick a recipient.");
      return;
    }
    if (selected.uid === proposerUid) {
      setErr("You cannot send a proposal to yourself.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const id = await createTradeProposal({
        proposerUid,
        recipientUid: selected.uid,
        sentByProposer,
        sentByRecipient,
        message: message.trim() ? message : null,
      });
      onCreated(id);
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Failed to create proposal.");
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;

  const sentTotal = computeTotal(sentByProposer);
  const recvTotal = computeTotal(sentByRecipient);
  const diff = recvTotal - sentTotal;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" aria-hidden={!open}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-proposal-title"
        className="relative z-10 w-full max-w-2xl rounded-md border border-gray-800 bg-gray-950 p-4 shadow-2xl"
        tabIndex={-1}
      >
        <div className="mb-3 flex items-center justify-between">
          <div id="send-proposal-title" className="text-lg font-semibold text-gray-100">Send Trade Proposal</div>
          <button className="rounded border border-gray-700 bg-black/40 px-2 py-1 text-gray-200 hover:bg-white/5" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex gap-2">
            <input
              ref={userSearchRef}
              value={qUser}
              onChange={(e) => setQUser(e.target.value)}
              placeholder="Search recipient by username…"
              className="flex-1 rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-100"
              onKeyDown={(e) => e.key === "Enter" && void searchUsers()}
            />
            <button
              className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5 disabled:opacity-60"
              disabled={!qUser.trim() || busy}
              onClick={() => void searchUsers()}
            >
              Search
            </button>
          </div>

          {users.length > 0 && (
            <div className="max-h-56 overflow-auto rounded border border-gray-800">
              <ul className="divide-y divide-gray-800">
                {users.map((u) => (
                  <li key={u.uid} className="flex items-center justify-between px-2 py-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-gray-100">@{u.username}</div>
                      {u.displayName && <div className="text-xs text-gray-400">{u.displayName}</div>}
                    </div>
                    <button
                      className={`rounded border px-2 py-1 text-xs ${
                        selected?.uid === u.uid
                          ? "border-amber-600 bg-amber-500/20 text-amber-200"
                          : "border-gray-700 bg-black/40 text-gray-200"
                      } hover:bg-white/5`}
                      onClick={() => setSelected(u)}
                    >
                      {selected?.uid === u.uid ? "Selected" : "Select"}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional message to recipient…"
            rows={3}
            className="w-full rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-100"
          />

          <div className="rounded border border-gray-800 bg-black/20 p-2 text-sm text-gray-300">
            You send {formatCurrency(sentTotal)} • You receive {formatCurrency(recvTotal)} • Diff:{" "}
            <span className={diff === 0 ? "text-gray-300" : diff > 0 ? "text-emerald-400" : "text-amber-400"}>
              {formatCurrency(diff)}
            </span>
          </div>

          {err && <div className="rounded border border-red-800 bg-red-900/20 p-2 text-sm text-red-200">{err}</div>}

          <div className="flex items-center justify-end gap-2">
            <button className="rounded border border-gray-700 bg-black/40 px-3 py-2 text-gray-200 hover:bg-white/5" onClick={onClose}>
              Cancel
            </button>
            <button
              className="bg-amber-500 px-3 py-2 font-medium text-black hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={!selected || busy}
              onClick={() => void submit()}
            >
              {busy ? "Sending…" : "Send Proposal"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
